import { queryOptions, useQuery } from '@tanstack/react-query';
import { getApiClientErrorStatus, hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { parseWarehouseSync, warehouseSyncKeys, type WarehouseSync } from './entity';

/**
 * @public The workspace's warehouse-sync config, or `null` when none is
 * configured.
 *
 * A 404 is the API's "not connected yet" signal, not a failure, so it resolves
 * to `null`. Every other status propagates — a 500 must not be indistinguishable
 * from an unconfigured workspace.
 */
export async function getWarehouseSync(workspaceId: number): Promise<WarehouseSync | null> {
  try {
    const data = await hydraUiApi.warehouse.getWarehouseSync({
      params: { path: { environment_id: String(workspaceId) } },
    });
    if (data.warehouse_sync == null) {
      return null;
    }
    return parseWarehouseSync(data.warehouse_sync);
  } catch (error) {
    if (getApiClientErrorStatus(error) === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * @public Always refetches on mount: connecting and disconnecting a sync both
 * happen on other routes (and, until the migration completes, in the embedded
 * app, whose writes this cache never sees), so a cached record is routinely
 * wrong on arrival.
 */
export const getWarehouseSyncQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: warehouseSyncKeys.detail(workspaceId, 'current'),
    queryFn: () => getWarehouseSync(workspaceId),
    staleTime: 0,
    // The integrations directory and connections tab fold this query into a
    // shared `isLoading` that gates their whole body, so the global 5xx retry
    // policy would hold both behind a spinner for seconds. Fail fast instead and
    // let them degrade to "no warehouse card", which is what they render anyway.
    retry: false,
  });

/** @public */
export const useWarehouseSync = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getWarehouseSyncQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getWarehouseSyncQueryOptions(workspaceId), ...queryConfig });
};
